import type { FunctionComponent } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  AspectRatio,
  Box,
  Button,
  Card,
  Group,
  Image,
  LoadingOverlay,
  Menu,
  Modal,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useTranslation } from 'i18n';
import { fetchAllItemPhotos } from 'helpers/itemPhotos';
import type { ItemPhotoRow } from 'helpers/itemPhotos';
import { logException } from 'helpers/loggers';
import PaginationControls from 'components/PaginationControls/PaginationControls';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import { usePaginationState } from 'hooks/usePaginationState';

import { EditIconSm } from '@chownow/pantry/icons';

import MenuEdit from '../MenuEdit';
import { buildPhotoSlots, getSlotPhoto, mergeItemPhotoUpdate } from './helpers';
import { PhotoFilters, ITEM_STATUS, SORT_ORDER, STATUS_VALUES } from './PhotoFilters';
import type { SortValue, StatusValue } from './PhotoFilters';
import { PhotoDrawer } from './PhotoDrawer';
import { usePhotoUpload } from './usePhotoUpload';

const NS = 'menuPhotos';

type PreviewPhoto = {
  name: string;
  slotLabel: string;
  ratio: string;
  imgSrc: string;
};

export type MenuPhotosProps = {
  // Injectable for stories; production uses the real API helper.
  fetchItemPhotos?: typeof fetchAllItemPhotos;
};

function ratioToNumber(ratio: string): number {
  const [w, h] = ratio.split(':').map(Number);
  return w / h;
}

export const MenuPhotos: FunctionComponent<MenuPhotosProps> = ({
  fetchItemPhotos = fetchAllItemPhotos,
}) => {
  const { t } = useTranslation(NS);
  const { menu, photoFetchKey, refetchItems } = useContext(MenuEdit.Context);

  const { page, pageSize, handleChangeFilters } = usePaginationState();
  const [itemList, setItemList] = useState<ItemPhotoRow[] | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusValue[]>([...STATUS_VALUES]);
  const [sortOrder, setSortOrder] = useState<SortValue>(SORT_ORDER.MENU);
  const [previewPhoto, setPreviewPhoto] = useState<PreviewPhoto | null>(null);
  const [previewOpened, { open: openPreview, close: closePreview }] = useDisclosure();
  const [pendingDeleteItem, setPendingDeleteItem] = useState<ItemPhotoRow | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure();

  const {
    selectedItem,
    photoDrawerOpened,
    openPhotoDrawer,
    closePhotoDrawer,
    handleUpload,
    handleDelete,
    handleSavePhoto,
  } = usePhotoUpload({
    companyId: menu?.companyId,
    merchantId: menu?.merchantId,
    menuUuid: menu?.uuid,
    onItemPhotoUpdated: updated => setItemList(prev => mergeItemPhotoUpdate(prev, updated)),
    onPhotoChanged: refetchItems,
  });
  const fetchedMenuRef = useRef<string | null>(null);
  // Monotonic counter so a superseded fetch (same menu or not) can never commit its result.
  const fetchGenRef = useRef(0);

  useEffect(() => {
    if (!photoFetchKey) return;
    fetchedMenuRef.current = null;
    setItemList(null);
  }, [photoFetchKey]);

  useEffect(() => {
    if (!menu?.companyId || !menu?.merchantId || !menu?.uuid) return;
    if (fetchedMenuRef.current === menu.uuid) return;
    const requestedUuid = menu.uuid;
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    fetchedMenuRef.current = requestedUuid;
    setFetchError(false);
    setItemList(null);

    void fetchItemPhotos({
      companyId: menu.companyId,
      merchantId: menu.merchantId,
      menuUuid: requestedUuid,
    })
      .then(rows => {
        if (fetchGenRef.current !== gen) return;
        setItemList(rows);
      })
      .catch(error => {
        if (fetchGenRef.current !== gen) return;
        logException({
          message: 'Failed to fetch menu item photos',
          error,
          context: {
            menuUuid: requestedUuid,
            status: (error as { response?: { status?: number } })?.response?.status,
          },
          tags: null,
        });
        fetchedMenuRef.current = null;
        setFetchError(true);
        setItemList([]);
      });
  }, [menu?.companyId, menu?.merchantId, menu?.uuid, photoFetchKey, retryKey]);

  const handleRetry = () => {
    fetchedMenuRef.current = null;
    setRetryKey(k => k + 1);
  };

  const allItems = itemList ?? [];

  const displayItems =
    !statusFilter.length || statusFilter.length === STATUS_VALUES.length
      ? allItems
      : allItems.filter(
          item =>
            (statusFilter.includes(ITEM_STATUS.ACTIVE) && item.enabled) ||
            (statusFilter.includes(ITEM_STATUS.HIDDEN) && !item.enabled),
        );

  const activeIntegrationNames = new Set<string>(
    (menu?.integrations ?? [])
      .filter((i: { isDeleted?: boolean }) => !i.isDeleted)
      .map((i: { integrationName: string }) => i.integrationName),
  );

  const photoSlots = buildPhotoSlots(activeIntegrationNames);

  // Menu order comes from the endpoint's default sort; name sort stays client-side
  // since the whole list is already loaded.
  const sortedItems =
    sortOrder === SORT_ORDER.ITEM_NAME
      ? [...displayItems].sort((a, b) => a.name.localeCompare(b.name))
      : displayItems;

  const totalPages = Math.ceil(sortedItems.length / pageSize);
  const pagedItems = sortedItems.slice((page - 1) * pageSize, page * pageSize);

  const handleStatusChange = (statuses: StatusValue[]) => {
    setStatusFilter(statuses);
    handleChangeFilters('page', 1);
  };

  const handleSortChange = (sort: SortValue) => {
    setSortOrder(sort);
    handleChangeFilters('page', 1);
  };

  const handleRequestDelete = (item: ItemPhotoRow) => {
    setPendingDeleteItem(item);
    openDeleteModal();
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteItem) void handleDelete(pendingDeleteItem);
  };

  return (
    <Card p="md" pos="relative">
      <LoadingOverlay visible={!!menu?.uuid && itemList === null} zIndex={1} />
      <Stack gap="md">
        <PhotoFilters onStatusChange={handleStatusChange} onSortChange={handleSortChange} />
        {fetchError && (
          <Alert color="red" variant="light">
            <Group justify="space-between">
              <Text variant="body-md">{t('MenuPhotos.loadPhotosError')}</Text>
              <Button size="xs" onClick={handleRetry}>
                {t('MenuPhotos.retryButton')}
              </Button>
            </Group>
          </Alert>
        )}
        <Table.ScrollContainer minWidth={400}>
          <Table style={{ minWidth: 0 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={200} pt="md" pr="sm">
                  {t('MenuPhotos.itemColumn')}
                </Table.Th>
                <Table.Th>{t('MenuPhotos.photosColumn')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pagedItems.map(item => {
                const hasPhoto = !!(item.imageUrl || item.photos?.length);
                return (
                  <Table.Tr key={item.uuid}>
                    <Table.Td w={200} py="md" pr="sm" style={{ verticalAlign: 'top' }}>
                      <Text variant="body-md">{item.name}</Text>
                    </Table.Td>
                    <Table.Td py="md">
                      <Group align="flex-start" gap="sm" wrap="nowrap">
                        {photoSlots.map(slot => {
                          const sizeKey = `${slot.w}x${slot.h}`;
                          const variant = getSlotPhoto(item.photos, slot);
                          // Fall back to the primary variant when the scalar imageUrl is blank
                          // but pre-cropped photos exist, so fallback slots aren't empty.
                          const imgSrc =
                            variant?.imageUrl || item.imageUrl || item.photos?.[0]?.imageUrl;

                          if (!imgSrc) {
                            const emptyBox = (
                              <Box
                                key={sizeKey}
                                w={slot.w}
                                h={slot.h}
                                bg="var(--surface-tertiary)"
                                bdrs="xs2"
                                style={{ flexShrink: 0, alignSelf: 'flex-start' }}
                              />
                            );
                            return photoSlots.length === 1 ? (
                              emptyBox
                            ) : (
                              <Tooltip key={sizeKey} label={slot.label} position="top">
                                {emptyBox}
                              </Tooltip>
                            );
                          }

                          const photoBox = (
                            <Box
                              key={sizeKey}
                              component="button"
                              type="button"
                              w={slot.w}
                              h={slot.h}
                              bg="var(--surface-tertiary)"
                              bdrs="xs2"
                              pos="relative"
                              style={{
                                overflow: 'hidden',
                                flexShrink: 0,
                                alignSelf: 'flex-start',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setPreviewPhoto({
                                  name: item.name,
                                  slotLabel: slot.label,
                                  ratio: slot.ratio,
                                  imgSrc,
                                });
                                openPreview();
                              }}
                              aria-label={t('MenuPhotos.previewPhotoLabel', {
                                slot: slot.label,
                                name: item.name,
                              })}
                            >
                              <Image
                                w={slot.w}
                                h={slot.h}
                                src={imgSrc}
                                fit="cover"
                                style={{ display: 'block' }}
                              />
                            </Box>
                          );
                          return photoSlots.length === 1 ? (
                            photoBox
                          ) : (
                            <Tooltip key={sizeKey} label={slot.label} position="top">
                              {photoBox}
                            </Tooltip>
                          );
                        })}
                        <Menu
                          position="bottom-start"
                          returnFocus={!deleteModalOpened && !photoDrawerOpened}
                        >
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              radius={4}
                              aria-label={t('MenuPhotos.moreActionsLabel', { name: item.name })}
                            >
                              <EditIconSm />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown w={200}>
                            {hasPhoto ? (
                              <>
                                <Menu.Item onClick={() => openPhotoDrawer(item)}>
                                  {t('MenuPhotos.replacePhotoItem')}
                                </Menu.Item>
                                <Menu.Item onClick={() => handleRequestDelete(item)}>
                                  {t('MenuPhotos.deletePhotoItem')}
                                </Menu.Item>
                              </>
                            ) : (
                              <Menu.Item onClick={() => openPhotoDrawer(item)}>
                                {t('MenuPhotos.addPhotoItem')}
                              </Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <PaginationControls
          handleChangeFilters={handleChangeFilters}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
        />
      </Stack>
      <Modal
        opened={previewOpened}
        onClose={closePreview}
        title={
          previewPhoto &&
          t('MenuPhotos.previewPhotoTitle', {
            name: previewPhoto.name,
            slot: previewPhoto.slotLabel,
          })
        }
        size="lg"
      >
        {previewPhoto && (
          <Stack gap="xs">
            <AspectRatio ratio={ratioToNumber(previewPhoto.ratio)}>
              <Image src={previewPhoto.imgSrc} alt={previewPhoto.name} fit="cover" />
            </AspectRatio>
          </Stack>
        )}
      </Modal>
      <ConfirmationModal
        title={t('MenuPhotos.deletePhotoModalTitle')}
        message={t('MenuPhotos.deletePhotoConfirmation')}
        onConfirm={handleConfirmDelete}
        opened={deleteModalOpened}
        close={closeDeleteModal}
      />
      <PhotoDrawer
        key={selectedItem?.uuid}
        opened={photoDrawerOpened}
        onClose={closePhotoDrawer}
        itemName={selectedItem?.name ?? ''}
        companyId={menu?.companyId != null ? String(menu.companyId) : undefined}
        menuId={menu?.uuid}
        itemId={selectedItem?.uuid}
        activeIntegrationNames={activeIntegrationNames}
        onUpload={handleUpload}
        onSave={handleSavePhoto}
      />
    </Card>
  );
};
